import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import { wz } from './wizard-tokens';

/** Bereich im Text, der kurz gelb hervorgehoben wird (Zeichenindizes). */
export interface TextHighlight {
  start: number;
  end: number;
}

interface Props extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'onChange' | 'value' | 'rows'
> {
  value: string;
  onChange: (value: string) => void;
  /** Mindesthöhe in Zeilen. */
  minRows?: number;
  highlight?: TextHighlight | null;
  /** Fokus + Markierung (oder Cursor bei start === end); wirkt bei jeder Änderung von `tick`. */
  select?: { start: number; end: number; tick: number } | null;
  invalid?: boolean;
}

/** Höhe, die den ganzen Text ohne inneres Scrollen zeigt. */
const fitHeight = (el: HTMLTextAreaElement): number => {
  const previous = el.style.height;
  // Nur über „auto“ misst scrollHeight auch das Schrumpfen korrekt.
  el.style.height = 'auto';
  const needed = el.scrollHeight;
  el.style.height = previous;
  return needed;
};

/**
 * Textfeld im Stil der Wizard-Felder, das mit dem Text mitwächst (bis zur vollen Länge, ohne inneres Scrollen)
 * und zusätzlich per Ziehgriff unten rechts vergrößert werden kann. Ein Teil des Textes kann kurz gelb
 * hervorgehoben werden (Spiegel-Ebene hinter dem durchsichtigen Feld).
 */
const WizAutoTextArea: React.FC<Props> = ({
  value,
  onChange,
  minRows = 6,
  highlight,
  select,
  invalid,
  className,
  style,
  ...rest
}) => {
  const { classes, cx } = useStyles();
  const ref = useRef<HTMLTextAreaElement>(null);

  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const needed = fitHeight(el);
    el.style.height = `${needed}px`;
  }, []);

  // Nach jeder Textänderung (Tippen, Einfügen, Link einlesen, Ergänzen) neu anpassen.
  useLayoutEffect(fit, [value, minRows, fit]);

  // Bei Breitenänderung (Fenstergröße) bricht der Text anders um.
  useEffect(() => {
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [fit]);

  // Fokus und Markierung nach dem Einfügen eines Vorschlags (z. B. „[Spanne eintragen]“ zum Überschreiben).
  const selectTick = select?.tick;
  useEffect(() => {
    const el = ref.current;
    if (!el || !select) return;
    el.focus({ preventScroll: true });
    el.setSelectionRange(select.start, select.end);
    // Nur bei neuem Tick; `value` ist dann schon im Feld.
  }, [selectTick]);

  const marked =
    highlight && highlight.end > highlight.start && highlight.end <= value.length ? highlight : null;

  return (
    <div className={classes.wrap}>
      {marked ? (
        <div className={classes.mirror} aria-hidden>
          {value.slice(0, marked.start)}
          <mark className={classes.mark}>{value.slice(marked.start, marked.end)}</mark>
          {value.slice(marked.end)}
        </div>
      ) : null}
      <textarea
        {...rest}
        ref={ref}
        className={cx(classes.textarea, invalid ? 'invalid' : undefined, className)}
        style={{ minHeight: `${minRows * 1.5 * 15 + 24}px`, ...style }}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
};

const useStyles = makeStyles()({
  wrap: { position: 'relative', width: '100%', background: wz.page, borderRadius: 10 },
  textarea: {
    position: 'relative',
    display: 'block',
    border: 'none',
    outline: 'none',
    // Manuell vergrößern/verkleinern; das automatische Mitwachsen setzt die Höhe beim nächsten Tippen neu.
    resize: 'vertical',
    overflow: 'auto',
    background: 'transparent',
    borderRadius: 10,
    padding: 12,
    fontSize: 15,
    lineHeight: 1.5,
    color: wz.text,
    fontFamily: 'inherit',
    width: '100%',
    boxSizing: 'border-box',
    '&.invalid': { boxShadow: `0 0 0 1px ${wz.redFg}` },
  },
  // Spiegel hinter dem Feld: gleiche Schrift/Abstände, damit der Treffer exakt hinter dem Text liegt.
  mirror: {
    position: 'absolute',
    inset: 0,
    padding: 12,
    fontSize: 15,
    lineHeight: 1.5,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    color: 'transparent',
    pointerEvents: 'none',
    overflow: 'hidden',
    borderRadius: 10,
  },
  mark: {
    color: 'transparent',
    background: '#FFE66D',
    borderRadius: 3,
    boxShadow: '0 0 0 2px #FFE66D',
    animation: 'wizFlash 3s ease-out forwards',
    '@keyframes wizFlash': {
      '0%': { background: '#FFD900', boxShadow: '0 0 0 2px #FFD900' },
      '70%': { background: '#FFE66D', boxShadow: '0 0 0 2px #FFE66D' },
      '100%': { background: 'rgba(255,230,109,0.35)', boxShadow: '0 0 0 2px rgba(255,230,109,0.35)' },
    },
  },
});

export default WizAutoTextArea;
